// Caso TEPROSIF-R pregrabado para la demo pública (TestCorrectionDemo).
//
// NO es inventado: se capturó de una corrección real (Test AZDL4DR1G) y los
// puntajes de abajo son exactamente los que devolvió RedPsi para ese protocolo
// —es/ases/eses/total y las cuatro categorías—, verificados contra el dict del
// server. Se recuenta en el propio replay (ver SUMMARY_CHECK) para que un
// futuro editor no pueda desalinear los totales de las marcas sin que salte.
//
// Se eligió un niño de 3 años con déficit porque es el caso donde el producto
// se ve: 63 procesos repartidos en 37 palabras es justo el conteo que a mano
// toma una tarde. Las emisiones van anonimizadas por construcción (el
// protocolo solo registra cómo se dijo cada palabra, nunca datos del menor).
//
// Los códigos se muestran tal cual, sin traducir: el catálogo de etiquetas del
// front (teprosifProcessOptions.PROC_GROUPS) no cubre S11+ y difiere del
// significado que el server les da a algunas E, así que la lectura clínica de
// la demo se toma de `funciones`, que es prosa del propio RedPsi.

/** Edad del protocolo, en el formato que usa FONO_AGE_OPTIONS. */
export const DEMO_AGE = "3";

export const DEMO_AGE_LABEL = "3 años";

/**
 * Emisión y procesos detectados por palabra, indexados por la `key` de
 * TEPROSIF_ITEMS. `emission` es lo que el niño dijo; `codes` son los procesos
 * que RedPsi detectó al corregir. Una palabra con emisión y sin códigos es
 * una emisión alterada que NO constituye proceso de simplificación fonológica
 * (dislalia), y se muestra así a propósito: es la distinción que el corrector
 * hace y el conteo a mano suele perder.
 */
export const DEMO_ITEMS = {
  Plancha: { emission: "pata", codes: ["E3", "E5", "E8", "S12"] },
  Rueda: { emission: "leda", codes: [] },
  Mariposa: { emission: "mamiposa", codes: [] },
  Bicicleta: { emission: "bisiketa", codes: ["E1"] },
  Helicoptero: { emission: "kikono", codes: ["E5", "E5", "A1", "S14"] },
  Bufanda: { emission: "bufama", codes: ["E5", "A2"] },
  Caperucita: { emission: "papulusita", codes: ["A1", "A1"] },
  Alfombra: { emission: "foma", codes: ["E1", "E5"] },
  Refrigerador: { emission: "bifememo", codes: ["E5", "E6", "E8", "A1"] },
  Edificio: { emission: "ebebejo", codes: ["E4", "E4", "E6"] },
  Calcetin: { emission: "kaketit", codes: ["E5", "A1", "A1"] },
  Dinosaurio: { emission: "diosayo", codes: ["E5", "E5", "E6"] },
  Telefono: { emission: "temejono", codes: ["A7", "A5"] },
  Remedio: { emission: "mememo", codes: ["E4"] },
  Peineta: { emission: "peyeta", codes: ["E6"] },
  Auto: { emission: "ato", codes: ["E2"] },
  Indio: { emission: "ino", codes: ["E2"] },
  Pantalon: { emission: "patanon", codes: ["E6", "E8"] },
  Camion: { emission: "payol", codes: ["E4", "A2", "S15"] },
  Cuaderno: { emission: "kuanelo", codes: ["E6", "E8"] },
  Micro: { emission: "miko", codes: [] },
  Tren: { emission: "te", codes: ["E6"] },
  Platano: { emission: "patano", codes: ["E1"] },
  Jugo: { emission: "juyo", codes: ["E5", "E7"] },
  Enchufe: { emission: "efuche", codes: ["E3", "E8"] },
  Jabon: { emission: "jabo", codes: ["E3"] },
  Tambor: { emission: "tamom", codes: ["E6"] },
  Volantin: { emission: "momati", codes: ["E3", "E3", "E5", "A1", "A1"] },
  Jirafa: { emission: "jibapa", codes: ["S5"] },
  Gorro: { emission: "gogo", codes: [] },
  Arbol: { emission: "abo", codes: ["E3"] },
  Dulce: { emission: "yushi", codes: ["E3", "E7", "A4"] },
  Guitarra: { emission: "guitala", codes: [] },
  Guante: { emission: "guate", codes: ["E3"] },
  Reloj: { emission: "lelol", codes: ["A1"] },
  Jaula: { emission: "jaya", codes: ["E5"] },
  Puente: { emission: "guete", codes: ["E4"] },
};

/**
 * Resultado tal cual lo entregó RedPsi (campos es/ases/eses/total y las
 * categorías cat_e/cat_a/cat_s/cat_g). Las tildes van escritas bien acá: el
 * server las devuelve corrompidas ("d�ficit") y eso es un defecto suyo, no
 * algo que la demo deba reproducir.
 */
export const DEMO_SUMMARY = {
  rows: [
    {
      key: "est",
      label: "Estructura silábica",
      total: 45,
      category: "Déficit severo",
      level: "severe",
    },
    {
      key: "asim",
      label: "Asimilación",
      total: 14,
      category: "Riesgo",
      level: "risk",
    },
    {
      key: "sust",
      label: "Sustitución",
      total: 4,
      category: "Normal",
      level: "ok",
    },
  ],
  total: 63,
  totalCategory: "Déficit",
  totalLevel: "deficit",
  // `quince`: categoría según los 15 procesos más frecuentes del baremo.
  fifteen: "Riesgo",
};

/**
 * Prosa del propio server (campo `funciones`): los procesos de simplificación
 * que el baremo destaca para este protocolo. Se muestran textuales para no
 * traducir códigos por cuenta propia.
 */
export const DEMO_FUNCTIONS = [
  "omisión de elementos átonos",
  "omisión de sílaba tónica o alguno de sus constituyentes",
  "omisión de consonante trabante",
  "asimilación idéntica",
  "inversión de fonemas o sílabas",
  "coalescencia",
  "reducción del grupo consonántico",
  "asimilación labial",
];

/** Prefijo del código -> grupo, para pintar cada chip con su color. */
export function codeGroup(code) {
  if (!code) return "est";
  if (code.charAt(0) === "E") return "est";
  if (code.charAt(0) === "A") return "asim";
  return "sust";
}

/**
 * Recuento de control: los totales de DEMO_SUMMARY tienen que salir de
 * DEMO_ITEMS. Si alguien edita una marca y se olvida del puntaje (o al revés),
 * esto lo delata en consola en desarrollo en vez de dejar una demo que miente.
 */
export function summaryCheck() {
  const counts = { est: 0, asim: 0, sust: 0 };
  Object.keys(DEMO_ITEMS).forEach((key) => {
    DEMO_ITEMS[key].codes.forEach((code) => {
      counts[codeGroup(code)] += 1;
    });
  });
  const declared = DEMO_SUMMARY.rows.reduce((acc, row) => {
    acc[row.key] = row.total;
    return acc;
  }, {});
  const mismatches = Object.keys(counts).filter(
    (key) => counts[key] !== declared[key]
  );
  const sum = counts.est + counts.asim + counts.sust;
  if (sum !== DEMO_SUMMARY.total) mismatches.push("total");
  return { counts, sum, mismatches };
}
